/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { FormControl, FormLabel as MuiFormLabel, RadioGroup } from '@material-ui/core';
import { FormLabel, Qualifier, Typography } from 'bento';
import {
  checkFormHasError,
  getFormHelperText,
  handleChangeFormField,
} from '@tock/shared/ui/forms/formHelpers';
import {
  formGroupRootStyles,
  formRadioButtonGroupHorizontal,
  formRadiobuttonGroupHelperText,
} from './formRadiobuttonGroupCssDefs';
import { useField } from 'formik';

export interface FormRadiobuttonGroupProps {
  classes?: {
    root?: string;
    radioGroupRoot?: string;
  };
  layout?: 'vertical' | 'horizontal';
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  name: string;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  'data-testid'?: string;
  defaultValue?: string | number | readonly string[] | undefined;
  qualifier?: Qualifier;
}

/**
 * @deprecated Deprecated — use pantry RadioGroup instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-radiogroup--docs
 * Migration: Replace FormRadiobuttonGroup with the pantry RadioGroup component wired to your form library.
 */
export const FormRadiobuttonGroup: React.FunctionComponent<
  React.PropsWithChildren<FormRadiobuttonGroupProps>
> = ({
  children,
  classes,
  disabled,
  fullWidth,
  helperText,
  id,
  label,
  layout = 'vertical',
  qualifier = 'default',
  ...rest
}) => {
  const [field, meta, helpers] = useField(rest as any);

  const hasError = checkFormHasError(meta);

  const helperTextValue = getFormHelperText(hasError, meta, helperText);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(field, event, rest.onChange as any);
    setTimeout(() => helpers.setTouched(true));
  };

  return (
    <FormControl
      className={classes?.root}
      css={layout === 'horizontal' && formRadioButtonGroupHorizontal}
      component="fieldset"
      error={hasError}
      disabled={disabled}
      fullWidth={fullWidth}
      data-testid={rest['data-testid']}
    >
      {label && (
        <MuiFormLabel component="legend">
          <FormLabel qualifier={qualifier} disabled={disabled} mb={0.5}>
            {label}
          </FormLabel>
        </MuiFormLabel>
      )}
      {helperTextValue && (
        <Typography variant="caption" css={formRadiobuttonGroupHelperText(disabled, hasError)}>
          {helperText}
        </Typography>
      )}
      <RadioGroup
        css={[formGroupRootStyles, layout === 'horizontal' && formRadioButtonGroupHorizontal]}
        classes={{
          root: classes?.radioGroupRoot,
        }}
        aria-label={label}
        id={id}
        name={field.name}
        defaultValue={rest.defaultValue}
        value={field.value}
        onChange={handleChange}
      >
        {React.Children.map(children, (child) => {
          return React.isValidElement(child)
            ? React.cloneElement(child as any, { disabled })
            : child;
        })}
      </RadioGroup>
    </FormControl>
  );
};
